import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { staffAttApi, errMsg } from '@/services/staffAttendanceApi';

const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const Result = ({ r }) => (
  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm space-y-1" data-testid="past-upload-result">
    <div><b>{r.file_name}</b> — {r.days} staff-days for <b data-testid="past-upload-staff">{r.staff}</b> staff ({r.date_from} → {r.date_to})</div>
    <div className="text-emerald-800" data-testid="past-upload-counts">{r.present} present · {r.absent} absent · {r.half_day} half day · {r.off} week off · {r.new} new / {r.updated} updated</div>
    {r.unmatched_count > 0 && <div className="text-amber-700" data-testid="past-upload-unmatched">{r.unmatched_count} Staff ID(s) not in Staff Master: {r.unmatched.slice(0, 15).join(', ')}{r.unmatched_count > 15 ? ' …' : ''}</div>}
    {r.bad_cells > 0 && <div className="text-amber-700" data-testid="past-upload-bad">{r.bad_cells} cell(s) skipped — use P, A, HD or WO: {r.bad_examples.join(' · ')}</div>}
    {r.future_skipped > 0 && <div className="text-gray-600">{r.future_skipped} future date cell(s) skipped</div>}
  </div>
);

const download = (blob, name) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 0);
};

const ym = (d) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }).slice(0, 7);
const defaultMonths = () => {
  const now = ym(new Date());
  const [y, m] = now.split('-').map(Number);
  const fy = `${m >= 4 ? y : y - 1}-04`;
  const prev = ym(new Date(Date.UTC(y, m - 2, 15)));
  return [fy, prev < fy ? fy : prev];
};

/** HR: day-wise Present / Absent sheet for months before eSSL logs — used only by Staff Rankings. */
export const PastAttendanceUpload = () => {
  const fileRef = useRef(null);
  const [from, setFrom] = useState(() => defaultMonths()[0]);
  const [to, setTo] = useState(() => defaultMonths()[1]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const loadHistory = () => staffAttApi.pastUploads().then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const getTemplate = async () => {
    setBusy('tpl');
    try { download(await staffAttApi.pastTemplate(from, to), `past_attendance_${from}_to_${to}.xlsx`); } catch (e) { toast.error(errMsg(e, 'Could not build the template')); } finally { setBusy(''); }
  };
  const upload = async () => {
    setBusy('up');
    try {
      const r = await staffAttApi.pastUpload(file);
      setResult(r); setFile(null); if (fileRef.current) fileRef.current.value = '';
      toast.success(`${r.days} staff-days saved`); loadHistory();
    } catch (e) { toast.error(errMsg(e, 'Could not read the sheet')); } finally { setBusy(''); }
  };

  return (
    <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="past-attendance-upload">
      <div className="font-semibold">Past attendance (P / A sheet)</div>
      <p className="text-xs text-gray-500">For months with no eSSL logs. Download the template (one sheet per month, Staff ID × date), fill <b>P</b> present, <b>A</b> absent, <b>HD</b> half day or <b>WO</b> week off, then upload. Used only for Staff Rankings — it does not change attendance or pay. Days with app / eSSL punches always use the punches. Re-uploading updates the same days.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[150px] bg-white" data-testid="past-from-month" />
        <span className="text-xs text-gray-500">to</span>
        <Input type="month" value={to} onChange={(e) => setTo(e.target.value)} className="w-[150px] bg-white" data-testid="past-to-month" />
        <Button variant="outline" onClick={getTemplate} disabled={!from || !to || !!busy} data-testid="past-template-btn">
          {busy === 'tpl' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Template
        </Button>
        <Input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-[280px] bg-white" data-testid="past-file-input" />
        <Button onClick={upload} disabled={!file || !!busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="past-upload-btn">
          {busy === 'up' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Upload
        </Button>
      </div>
      {result && <Result r={result} />}
      {history.length > 0 && (
        <div className="text-xs text-gray-500 space-y-0.5" data-testid="past-upload-history">
          <div className="font-semibold text-gray-600">Recent uploads</div>
          {history.slice(0, 5).map((h) => (
            <div key={h.upload_id} data-testid={`past-history-${h.upload_id}`}>{fmtAt(h.uploaded_at)} · {h.file_name} · {h.days} staff-days / {h.staff} staff · {h.date_from}→{h.date_to} · {h.uploaded_by_name}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PastAttendanceUpload;
